import type { Card } from "@joker-holdem/core";
import { rankChar, suitClass, suitSymbol } from "./cardUtils.js";

export type CardSize = "xs" | "sm" | "md" | "lg";

interface CardViewProps {
  /** Concrete card, or undefined for a face-down/placeholder slot. */
  card?: Card;
  /** Render the special joker face instead of a real card. */
  joker?: boolean;
  size?: CardSize;
  faceDown?: boolean;
  /**
   * Renders a patterned card-back (e.g. the opponent's known-but-hidden
   * hole cards) instead of an empty dashed placeholder -- makes it visually
   * obvious a real card is there, just not shown (docs/UI_REFERENCE.md
   * "フィードバック反映" #5).
   */
  back?: boolean;
  /** Extra class name(s), e.g. for positioning a joker-interpretation card. */
  className?: string;
}

export function CardView({ card, joker, size = "md", faceDown, back, className }: CardViewProps) {
  const sizeClass = `card-${size}`;
  const extra = className ? ` ${className}` : "";

  if (back) {
    return <div className={`card card-back ${sizeClass}${extra}`} aria-label="face-down card" />;
  }

  if (joker && !faceDown) {
    return (
      <div className={`card card-joker ${sizeClass}${extra}`} aria-label="Joker">
        <span className="card-joker-star">★</span>
        <span className="card-joker-text">JOKER</span>
      </div>
    );
  }

  if (card === undefined || faceDown) {
    return <div className={`card card-placeholder ${sizeClass}${extra}`} aria-hidden="true" />;
  }

  return (
    <div className={`card ${suitClass(card)} ${sizeClass}${extra}`}>
      <span className="card-rank">{rankChar(card)}</span>
      <span className="card-suit">{suitSymbol(card)}</span>
    </div>
  );
}
